import {inject, Injectable, signal} from '@angular/core';
import {
  GetChatUserInfoControllerService,
  ProviderTenderView,
  TenderStatus,
  TokenFetcherControllerService,
} from '@frontend/shared-api';
import Talk from 'talkjs';
import {firstValueFrom} from 'rxjs';
import {ToastService} from '@frontend/toast';
import {TenderPhaseUtils} from '../../../../../phase/tender-phase.util';

@Injectable({providedIn: 'root'})
export class ProviderTenderCardChatService {
  private readonly getChatUserInfoService = inject(GetChatUserInfoControllerService);
  private readonly tokenFetcherService = inject(TokenFetcherControllerService);
  private readonly toast = inject(ToastService);

  private popup?: Talk.Popup;
  private currentTenderId?: number;

  readonly loading = signal(false);
  readonly open = signal(false);

  isTenderChatClosed(tender: ProviderTenderView): boolean {
    const currentStatus = tender.activePhaseStatusInfo.status;
    const hasLost =
      currentStatus === TenderStatus.EXPIRED ||
      currentStatus === TenderStatus.REJECTED ||
      currentStatus === TenderStatus.LOST ||
      (tender.hasLostContract ?? false);
    const hasWon =
      currentStatus === TenderStatus.FINISHED &&
      tender.activePhaseStatusInfo.phase === TenderPhaseUtils.LAST_TENDER_PHASE;
    return hasLost || hasWon;
  }

  async openChat(tenderMappingId: number): Promise<void> {
    if (this.popup && this.currentTenderId === tenderMappingId) {
      this.popup.show();
      this.open.set(true);
      return;
    }

    this.loading.set(true);
    try {
      await this.initializeChat(tenderMappingId);
      this.currentTenderId = tenderMappingId;
      this.popup?.show();
      this.open.set(true);
    } catch (error) {
      await this.toast.showError('Failed to initialize chat', 'Could not open chat');
      console.error('Failed to initialize chat:', error);
    } finally {
      this.loading.set(false);
    }
  }

  private async initializeChat(tenderMappingId: number): Promise<void> {
    const chatUserInfo = await firstValueFrom(
      this.getChatUserInfoService.getChatUserInfoByTender({
        tenderId: tenderMappingId,
      }),
    );

    await Talk.ready;

    const session = new Talk.Session({
      appId: chatUserInfo.appId!,
      userId: chatUserInfo.chatUserId,
      tokenFetcher: () => this.fetchChatToken(),
    });

    const conversation = session.getOrCreateConversation(chatUserInfo.conversationId);
    conversation.setAttributes({
      subject: 'Tender Discussion',
    });

    this.popup = session.createPopup({
      launcher: 'close-only',
    });
    this.popup.select(conversation);
    this.popup.mount({show: false});

    this.popup.on('open', () => {
      this.open.set(true);
    });

    this.popup.on('close', () => {
      this.open.set(false);
    });
  }

  private async fetchChatToken(): Promise<string> {
    const response = await firstValueFrom(this.tokenFetcherService.fetchToken());
    return response.token;
  }
}
